// SPDX-License-Identifier: Elastic-2.0
/**
 * TierBadge — small uppercase pill that marks a sidebar entry (or any
 * surface) as belonging to the Plus or Pro tier.
 *
 * Purely presentational at MVP: no entitlement check, no gating. The
 * badge always renders for the caller-specified tier. Sibling
 * navigation entries carry the tier in their own `aria-label`, so this
 * pill is `aria-hidden` to avoid double-announcing.
 *
 * Visual contract: peach for `"plus"`, lavender for `"pro"`, both
 * styled by `TierBadge.module.css` against the existing design-system
 * tokens. See functional spec § 3 and technical spec § 2 (F-330).
 */
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import styles from "./TierBadge.module.css";

export type TierBadgeKind = "plus" | "pro";

export interface TierBadgeProps {
  kind: TierBadgeKind;
  /** Override the rendered text. Defaults to `t(\`tierBadge.${kind}\`)`. */
  label?: string;
  className?: string;
}

export function TierBadge({ kind, label, className }: TierBadgeProps): JSX.Element {
  const { t } = useTranslation();
  const text = label ?? t(`tierBadge.${kind}`);
  const composed = [styles.badge, styles[kind], className]
    .filter(Boolean)
    .join(" ");
  return (
    <span className={composed} data-testid={`tier-badge-${kind}`} aria-hidden="true">
      {text}
    </span>
  );
}
